import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { faqContact, faqItems } from '../content/faq'
import { getBrand } from '../utils/brand'

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      className={`h-[18px] w-[18px] flex-none text-[#8A6D1D] transition-transform duration-300 motion-reduce:transition-none ${
        open ? 'rotate-180' : ''
      }`}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M3 5.5 8 10.5 13 5.5"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export default function FaqPage() {
  const { t, i18n } = useTranslation()
  // First question open by default; single-expand (opening one collapses the previous).
  const [openIndex, setOpenIndex] = useState(0)
  const lang = i18n.language?.startsWith('ms') ? 'ms' : 'en'
  const brand = getBrand()
  const withBrand = (text: string) => text.replace(/\{brand\}/g, brand.name)

  // Arriving from the landing footer keeps the old scroll position; start at the top.
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="bg-white">
      <section className="px-6 py-12 md:py-20">
        <div className="mx-auto max-w-3xl">
          <div className="mx-auto mb-10 max-w-2xl text-center md:mb-14">
            <span className="inline-flex items-center justify-center gap-2.5 text-[11px] font-bold uppercase tracking-[0.28em] text-[#8A6D1D]">
              <span className="h-px w-[34px] bg-[#C2A14D]" aria-hidden="true" />
              {t('faq.eyebrow')}
              <span className="h-px w-[34px] bg-[#C2A14D]" aria-hidden="true" />
            </span>
            <h1 className="mt-4 font-[CormorantGaramond] text-[34px] font-bold leading-tight text-[#12100E] md:text-[44px]">
              {t('faq.title')}
            </h1>
            <p className="mt-3 text-[15px] leading-relaxed text-[#12100EB2]">{t('faq.lede')}</p>
          </div>

          <div>
            {faqItems.map((item, i) => {
              const entry = item[lang]
              const open = openIndex === i
              return (
                <div
                  key={item.id}
                  className={`border-t border-neutral-200 ${i === faqItems.length - 1 ? 'border-b' : ''}`}
                >
                  <h3>
                    <button
                      type="button"
                      aria-expanded={open}
                      aria-controls={`faq-panel-${item.id}`}
                      id={`faq-trigger-${item.id}`}
                      onClick={() => setOpenIndex(open ? -1 : i)}
                      className="flex w-full items-center justify-between gap-4 py-5 text-left text-[16px] font-semibold text-[#12100E] transition-colors hover:text-[#0A5C26] md:text-[16.5px]"
                    >
                      <span>{withBrand(entry.question)}</span>
                      <Chevron open={open} />
                    </button>
                  </h3>
                  <div
                    id={`faq-panel-${item.id}`}
                    role="region"
                    aria-labelledby={`faq-trigger-${item.id}`}
                    className={`grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none ${
                      open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                    }`}
                  >
                    <div className="overflow-hidden">
                      <p className="max-w-[680px] pb-6 text-[15px] leading-relaxed text-[#12100EB2]">
                        {withBrand(entry.answer)}
                      </p>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          <div className="mt-12 rounded-2xl bg-[#E6F3EA] px-6 py-10 text-center md:mt-16 md:px-10">
            <h2 className="font-[CormorantGaramond] text-[26px] font-bold text-[#12100E] md:text-[30px]">
              {t('faq.contactTitle')}
            </h2>
            <p className="mx-auto mt-2 max-w-md text-[14.5px] leading-relaxed text-[#12100EB2]">
              {t('faq.contactBody')}
            </p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href={`mailto:${faqContact.email}`}
                className="inline-flex w-full items-center justify-center rounded-[10px] bg-[#0D7330] px-6 py-2.5 text-[15px] font-semibold text-white shadow-sm transition-colors hover:bg-[#0A5C26] sm:w-auto"
              >
                <span className="font-normal opacity-80">{t('faq.emailLabel')}:</span>
                <span className="ml-1.5">{faqContact.email}</span>
              </a>
              <a
                href={faqContact.whatsapp}
                target="_blank"
                rel="noreferrer"
                className="inline-flex w-full items-center justify-center rounded-[10px] border-[1.5px] border-[#0D7330] px-6 py-2.5 text-[15px] font-semibold text-[#0D7330] transition-colors hover:bg-[#0D7330] hover:text-white sm:w-auto"
              >
                <span className="font-normal opacity-80">{t('faq.phoneLabel')}:</span>
                <span className="ml-1.5">{faqContact.phoneDisplay}</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
